<template>
  <div v-if="items.length > 0 || showFinished" ref="rootRef" class="content-summary-processing-indicator">
    <div v-if="showFinished && items.length === 0" class="content-summary-processing-indicator__finished">
      <IconEmbedded name="hourglass_2" :size="18" class="content-summary-processing-indicator__icon" />
      <span>Finished processing</span>
    </div>
    <button v-if="items.length > 0" type="button" class="content-summary-processing-indicator__trigger" :aria-expanded="isExpanded" @click="isExpanded = !isExpanded">
      <IconEmbedded name="hourglass_2" :size="18" class="content-summary-processing-indicator__icon" />
      <span class="content-summary-processing-indicator__count"> {{ items.length }} {{ items.length === 1 ? "album" : "albums" }} processing </span>
      <IconEmbedded :name="isExpanded ? 'caret-bottom_2-5' : 'caret-top_2-5'" :size="18" />
    </button>
    <div v-if="isExpanded" class="content-summary-processing-indicator__list">
      <div v-for="item in items" :key="item.id" class="content-summary-processing-indicator__item">
        <span :title="getContextTypeFriendlyName(item.contextType)" class="content-summary-processing-indicator__item-icon-wrap">
          <IconEmbedded :name="getContextTypeIconName(item.contextType)" :size="16" color="#3583bb" class="content-summary-processing-indicator__item-icon" />
        </span>
        <span class="content-summary-processing-indicator__item-name">{{ displayName(item) }}</span>
      </div>
      <p class="content-summary-processing-indicator__note">Posts can use new content once processed.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import type { ContentSummaryContextType, ContentSummaryProcessingItem } from "@contracts/contentCalendar";
import type { IconName } from "@contracts/IconName";

const props = defineProps<{
  items: Array<ContentSummaryProcessingItem>;
}>();

const rootRef = ref<HTMLElement | null>(null);
const isExpanded = ref(false);
const showFinished = ref(false);
let finishedTimer: ReturnType<typeof setTimeout> | null = null;

watch(
  () => props.items,
  (newItems, oldItems) => {
    if ((oldItems?.length ?? 0) > 0 && newItems.length === 0) {
      if (finishedTimer !== null) clearTimeout(finishedTimer);
      showFinished.value = true;
      finishedTimer = setTimeout(() => {
        showFinished.value = false;
        finishedTimer = null;
      }, 20_000);
    }
  }
);

function getContextTypeIconName(contextType: ContentSummaryContextType | string): IconName {
  switch (contextType) {
    case "CreatorVisit":
      return "briefcase_2";
    case "Location":
      return "map-pin_2";
    case "CustomerUpload":
      return "upload_2";
    case "CommunityUpload":
      return "users_2";
    default:
      return "upload_2";
  }
}

function getContextTypeFriendlyName(contextType: ContentSummaryContextType | string): string {
  switch (contextType) {
    case "CreatorVisit":
      return "Creator Visit";
    case "Location":
      return "Visit Location";
    case "CustomerUpload":
      return "Your Uploads";
    case "CommunityUpload":
      return "Community Uploads";
    default:
      return "Upload";
  }
}

function handleClickOutside(e: MouseEvent) {
  if (rootRef.value && !rootRef.value.contains(e.target as Node)) {
    isExpanded.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  if (finishedTimer !== null) clearTimeout(finishedTimer);
});

function displayName(item: ContentSummaryProcessingItem): string {
  if (item.contextName?.trim()) return item.contextName;
  if (item.contextId?.trim()) return item.contextId;
  return item.id ?? "—";
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

.content-summary-processing-indicator {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;

  &__finished {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    font-size: 14px;
    color: #1a6b3a;
    background-color: #e6f5ec;
    border: 1px solid rgba(26, 107, 58, 0.25);
    border-radius: 8px;
  }

  &__finished-icon {
    flex-shrink: 0;
  }

  &__trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    font-size: 14px;
    color: #9a5a17;
    background-color: #fff3e3;
    border: 1px solid rgba(154, 90, 23, 0.2);
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background-color: #ffecd4;
    }
  }

  &__icon {
    flex-shrink: 0;
  }

  &__count {
    white-space: nowrap;
  }

  &__list {
    position: absolute;
    bottom: 100%;
    left: -20px;
    margin-bottom: 6px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 14px;
    background: rgba(255, 255, 255, 0.98);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    max-height: 240px;
    overflow-y: auto;

    @media (max-width: $mobile-max-width) {
      left: auto;
      right: 0;
      max-width: calc(100vw);
    }
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    padding: 4px 0;
  }

  &__item-name {
    font-weight: 500;
    color: rgba(0, 0, 0, 0.85);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__item-icon-wrap {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }

  &__item-icon {
    color: #3583bb;
  }

  &__note {
    margin: 8px 0 0;
    padding-top: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    font-size: 13px;
    font-style: italic;
    white-space: nowrap;
    // color: rgba(0, 0, 0, 0.5);
  }
}
</style>
